import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { processImageUrl } from '../../../utils/imageUtils';
import { NewspaperIcon } from '@heroicons/react/24/outline';

function OperationLink({ item, className, children }) {
  const navigate = useNavigate();
  const hasLink = Boolean(item?.linkUrl?.trim());

  const handleClick = useCallback(() => {
    const url = item.linkUrl.trim();
    if (url.startsWith('http://') || url.startsWith('https://')) {
      if (item.linkTarget === '_blank') {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = url;
      }
      return;
    }
    navigate(url.startsWith('/') ? url : `/${url}`);
  }, [item, navigate]);

  if (!hasLink) {
    return <div className={className}>{children}</div>;
  }

  return (
    <button type="button" onClick={handleClick} className={`${className} text-left w-full`}>
      {children}
    </button>
  );
}

function TopicBlock({ items }) {
  if (!items.length) return null;

  return (
    <div className="mb-0">
      <div className="text-sm font-semibold text-gray-700 mb-3">专题推荐</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((item) => {
          const src = processImageUrl(item.imageUrl);
          return (
            <OperationLink
              key={item.id}
              item={item}
              className="group rounded-lg overflow-hidden border border-gray-200 bg-white hover:border-green-300 transition-colors min-w-0"
            >
              <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                {src ? (
                  <img
                    src={src}
                    alt={item.title || '专题'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs">
                    暂无图片
                  </div>
                )}
              </div>
              <div className="p-3 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {item.title || '专题'}
                </div>
                {item.subtitle && (
                  <div className="text-xs text-gray-600 truncate mt-1">
                    {item.subtitle}
                  </div>
                )}
              </div>
            </OperationLink>
          );
        })}
      </div>
    </div>
  );
}

function EmptyTopics() {
  const navigate = useNavigate();
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-6 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 shrink-0">
        <NewspaperIcon className="h-5 w-5 text-gray-500" aria-hidden />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-gray-800">还没有专题内容</div>
        <p className="text-sm text-gray-600 mt-1">先去看看农业资讯。</p>
      </div>
      <button
        type="button"
        onClick={() => navigate('/agri-news')}
        className="shrink-0 text-sm font-semibold text-white bg-green-600 rounded-lg px-4 py-2 hover:bg-green-700"
      >
        浏览资讯
      </button>
    </div>
  );
}

/**
 * 专题运营位：数据由 WelcomePage 单次加载后经 props 传入，组件内不再请求接口。
 */
export default function HomeOperationSection({
  topics = [],
  loading = false,
  rightSlot = null,
}) {
  const hasTopics = Array.isArray(topics) && topics.length > 0;

  return (
    <section aria-label="首页专题位" className="min-w-0">
      {rightSlot ? <div className="mb-4">{rightSlot}</div> : null}
      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-6 flex items-center justify-center">
          <div className="animate-spin h-5 w-5 border-2 border-green-200 border-t-green-600 rounded-full" />
          <span className="ml-2 text-sm text-gray-600">运营位加载中…</span>
        </div>
      ) : hasTopics ? (
        <TopicBlock items={topics} />
      ) : (
        <EmptyTopics />
      )}
    </section>
  );
}
